/**
 * useDisplayProjects
 *
 * Derives the list of projects to display from raw myProjects by:
 * - Normalizing accessMaps
 * - Applying tab-based filtering (My Projects vs Discovery)
 *
 * Tag filtering and sort order come from the backend (paginated list API).
 */

import { useCallback } from 'react';
import { useAccessMapNormalization } from '../../../hooks/accessMap/useAccessMapNormalization';
import { useAccessMapFiltering } from '../../../hooks/accessMap/useAccessMapFiltering';
import { TAB_NAMES } from '../../../utils/accessMapConstants';
import { filterOutArchivedForNonOwners } from '../../../utils/archivedItemAccess';

/**
 * @param {Array} myProjects - Raw projects from API
 * @param {string} activeTab - Current tab (e.g. TAB_NAMES.MY_PROJECTS, TAB_NAMES.DISCOVERY)
 * @param {Object|null} currentUser - Current user (for normalization and tab filtering)
 * @returns {function(): Array} getDisplayProjects - Function that returns the derived display list
 */
export function useDisplayProjects(myProjects, activeTab, currentUser) {
  const { normalizeAccessMap } = useAccessMapNormalization(currentUser);
  const { filterByTab: filterByTabHook } = useAccessMapFiltering(
    currentUser?.username,
    currentUser
  );

  const getDisplayProjects = useCallback(() => {
    let filteredProjects = myProjects.map((project) => {
      if (project.accessMap) {
        const normalized = normalizeAccessMap(project.accessMap);
        return {
          ...project,
          accessMap: normalized,
        };
      }
      return project;
    });

    if (activeTab !== TAB_NAMES.DISCOVERY) {
      filteredProjects = filterByTabHook(filteredProjects, activeTab);
    }

    // Contributors / viewers must not see archived projects (owners only).
    filteredProjects = filterOutArchivedForNonOwners(
      filteredProjects,
      currentUser?.username,
      currentUser
    );

    return filteredProjects;
  }, [myProjects, activeTab, normalizeAccessMap, filterByTabHook, currentUser]);

  return getDisplayProjects;
}
